import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH } from '@lizrd/contracts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { AuthStatusIcon } from '../components/auth/AuthStatusIcon';
import { PasswordRequirements, isPasswordValid } from '../components/auth/PasswordRequirements';
import { resetPassword } from '../lib/api';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'This reset link is invalid or has expired');
    } finally {
      setBusy(false);
    }
  }

  // No token → the link is malformed.
  if (!token) {
    return (
      <Card className="flex flex-col items-center gap-4 p-8 text-center">
        <AuthStatusIcon variant="error" />
        <div>
          <h1 className="font-display text-xl font-bold text-bone">This link didn't work</h1>
          <p className="mt-1 text-sm text-muted">
            The reset link is invalid or incomplete. Request a fresh one.
          </p>
        </div>
        <Link to="/forgot-password" className="text-sm text-iris hover:text-iris-strong">
          Request a new link
        </Link>
      </Card>
    );
  }

  if (done) {
    return (
      <Card className="flex flex-col items-center gap-4 p-8 text-center">
        <AuthStatusIcon variant="success" />
        <div>
          <h1 className="font-display text-xl font-bold text-bone">Password updated</h1>
          <p className="mt-1 text-sm text-muted">You can now sign in with your new password.</p>
        </div>
        <Button onClick={() => navigate('/login', { replace: true })} className="mt-1 w-full">
          Continue to sign in
        </Button>
      </Card>
    );
  }

  return (
    <Card className="space-y-5 p-8">
      <div>
        <h1 className="font-display text-xl font-bold text-bone">Choose a new password</h1>
        <p className="mt-1 text-sm text-muted">Pick something strong you'll remember.</p>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <p className="text-sm text-danger">{error}</p>}
        <Input
          type="password"
          required
          minLength={PASSWORD_MIN_LENGTH}
          placeholder="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordRequirements value={password} />
        <Button type="submit" disabled={busy || !isPasswordValid(password)} className="w-full">
          {busy ? 'Updating…' : 'Update password'}
        </Button>
      </form>
      <p className="text-sm">
        <Link to="/login" className="text-iris hover:text-iris-strong">
          Back to sign in
        </Link>
      </p>
    </Card>
  );
}
